/**
 * WHAT THE WINTER DID TO THE GARAGE. D-EZ-62.
 *
 * Howard, 19 Aug 2026: *"we just rolled over season 1 and had our winter where
 * some lost their license. It would seem we lost some garage's/teams but the
 * game has to make new garage's and teams to compete and have spots for rookies
 * as well in addition to independents no? Unless Im crazy and we didnt actually
 * lose any teams."*
 *
 * ===========================================================================
 * HE WAS NOT CRAZY, AND HE HAD NOT LOST ANY TEAMS
 * ===========================================================================
 *
 * Measured on his own league — RASCAL, seed 424242, season 1: fourteen shops
 * and forty-two chartered seats before the winter, fourteen shops and forty-two
 * chartered seats after it. Thirteen men lost their licence, thirteen rookies
 * were signed, ten charter seats emptied and all ten were refilled — eight of
 * them by independents moving up, two by rookies — and the eleven independents
 * next season are eleven rookies starting where privateers start.
 *
 * ⚠ EVERY BIT OF THAT ALREADY HAPPENED AND NONE OF IT WAS ON SCREEN. The Winter
 * view counted who left and how many rookies arrived and then stopped, so a
 * player watching thirteen names disappear had no way to learn that the field
 * was whole — which is precisely why he asked. A season report that shows the
 * losses and not the replacements is not neutral; it reads as a league quietly
 * emptying out.
 *
 * ===========================================================================
 * DERIVED, NEVER STORED
 * ===========================================================================
 *
 * Everything below is a reading of two lists the offseason already produced —
 * the garage as it was and the garage as it is — plus the rookie roll. There is
 * no new state and nothing to migrate: a league saved before this existed opens
 * with the whole story in it, because the story was always in there.
 */

import type { CareerSeason } from '../lib/careerRunner.ts';
import type { DriverCard } from '../domain/types.ts';

/** How a man came to be in the seat he is in. */
export type SeatOrigin =
  /** He was in this shop last season and still is. */
  | 'stayed'
  /** Signed this winter, on the inaugural tables. */
  | 'rookie'
  /** Ran without a charter last season and has been picked up. */
  | 'independent'
  /** Moved across from another shop. */
  | 'moved';

export interface Seat {
  driverId: string;
  name: string;
  origin: SeatOrigin;
  /** The shop he came from, when he moved. */
  fromTeam: string | null;
}

/** Somebody who was in this shop last season and is not in it now. */
export interface Departure {
  driverId: string;
  name: string;
  /**
   * Why he is not here: lost his licence, retired (D-EZ-93), was killed during
   * the season, or simply moved to another shop / his own car.
   */
  reason: 'licence' | 'retired' | 'died' | 'moved';
  /** Where he went, when he went somewhere. */
  toTeam: string | null;
}

export interface ShopLine {
  teamId: string;
  name: string;
  /** The name it ran under last season, when the charter changed hands. */
  wasCalled: string | null;
  /** Why it changed hands, in the offseason's own words. */
  soldStory: string | null;
  crew: string | null;
  seats: Seat[];
  gone: Departure[];
}

export interface Privateer {
  driverId: string;
  name: string;
  rookie: boolean;
}

export interface GarageChange {
  shops: ShopLine[];
  independents: Privateer[];
  /** Charters that were closed outright rather than sold — `onFailure: 'close'`. */
  closed: { teamId: string; name: string; story: string }[];
  counts: {
    shopsBefore: number;
    shopsAfter: number;
    seatsBefore: number;
    seatsAfter: number;
    independentsAfter: number;
    rosterAfter: number;
    /** Charter seats that emptied and were filled again. */
    refilled: number;
    rookiesIntoCharters: number;
    lostLicence: number;
    /** D-EZ-93 — walked away this winter. */
    retired: number;
    /** Killed during the season (TV EVENT Z 13, Super Rare). */
    died: number;
    /** Of the deaths, how many seats a rookie already filled mid-season. */
    replacedMidSeason: number;
    /**
     * Rookies licensed this winter. INVARIANT: one for every seat that emptied
     * for any reason — lost licence, retirement, death — less the deaths a
     * rookie already replaced during the season.
     */
    rookiesSigned: number;
    /** Shops that changed hands but kept their cars. */
    sold: number;
  };
}

/**
 * ⚠ A BOUGHT CHARTER KEEPS ITS IDENTITY, UNDER A NEW NAME.
 *
 * `settleTheGarage` gives a rebranded shop the id `<old>-s<n+1>`, so the two
 * are the same charter and must be shown as one shop that changed hands rather
 * than as one that vanished and one that appeared. Matching on the prefix is
 * how the two are tied together, and it is the difference between "Jessup
 * Brothers folded" and "Jessup Brothers was bought and races as Ridgeline".
 */
function wasThisShop(afterId: string, beforeId: string): boolean {
  return afterId === beforeId || afterId.startsWith(`${beforeId}-s`);
}

export function garageChange(career: CareerSeason): GarageChange {
  const before = career.teams;
  const { offseason } = career;
  const after = offseason.teams;

  const nameOf = new Map<string, string>();
  for (const card of career.roster) nameOf.set(card.driverId, card.name);
  for (const card of offseason.roster) nameOf.set(card.driverId, card.name);
  for (const d of offseason.demotions) nameOf.set(d.driverId, d.name);
  for (const r of offseason.retirements) nameOf.set(r.driverId, r.name);
  for (const f of career.season.fatalities) nameOf.set(f.driverId, f.name);
  const named = (id: string): string => nameOf.get(id) ?? id;

  const rookies = new Set(offseason.rookies.map((r) => r.card.driverId));
  const lostLicence = new Set(offseason.demotions.map((d) => d.driverId));
  const retired = new Set(offseason.retirements.map((r) => r.driverId));
  const died = new Set(career.season.fatalities.map((f) => f.driverId));

  /** driverId → the shop he was in last season. Absent means he ran independent. */
  const shopWas = new Map<string, string>();
  for (const team of before) for (const id of team.driverIds) shopWas.set(id, team.name);

  /** driverId → the shop he is in now. */
  const shopNow = new Map<string, string>();
  for (const team of after) for (const id of team.driverIds) shopNow.set(id, team.name);

  const soldBy = new Map<string, { name: string; story: string }>();
  for (const failure of offseason.teamFailures) {
    if (failure.replacedBy) {
      soldBy.set(failure.replacedBy.teamId, { name: failure.name, story: failure.description });
    }
  }

  let refilled = 0;
  let rookiesIntoCharters = 0;

  const shops: ShopLine[] = after.map((team) => {
    const was = before.find((b) => wasThisShop(team.teamId, b.teamId));
    const held = new Set(was?.driverIds ?? []);

    const seats: Seat[] = team.driverIds.map((id) => {
      if (held.has(id)) return { driverId: id, name: named(id), origin: 'stayed', fromTeam: null };
      refilled += 1;
      if (rookies.has(id)) {
        rookiesIntoCharters += 1;
        return { driverId: id, name: named(id), origin: 'rookie', fromTeam: null };
      }
      const from = shopWas.get(id);
      return {
        driverId: id,
        name: named(id),
        // No shop last season means he was running his own car — see `field.ts`,
        // where anyone not dealt a charter seat is a privateer by definition.
        origin: from ? 'moved' : 'independent',
        fromTeam: from ?? null,
      };
    });

    const holds = new Set(team.driverIds);
    const gone: Departure[] = (was?.driverIds ?? [])
      .filter((id) => !holds.has(id))
      .map((id) => ({
        driverId: id,
        name: named(id),
        reason: lostLicence.has(id)
          ? ('licence' as const)
          : retired.has(id)
            ? ('retired' as const)
            : died.has(id)
              ? ('died' as const)
              : ('moved' as const),
        toTeam: shopNow.get(id) ?? null,
      }));

    const sold = soldBy.get(team.teamId) ?? null;
    return {
      teamId: team.teamId,
      name: team.name,
      wasCalled: sold?.name ?? null,
      soldStory: sold?.story ?? null,
      crew: offseason.crewGrades[team.teamId] ?? null,
      seats,
      gone,
    };
  });

  const seated = new Set(after.flatMap((t) => t.driverIds));
  const independents: Privateer[] = offseason.roster
    .filter((card: DriverCard) => !seated.has(card.driverId))
    .map((card) => ({ driverId: card.driverId, name: card.name, rookie: rookies.has(card.driverId) }));

  const closed = offseason.teamFailures
    .filter((f) => !f.replacedBy)
    .map((f) => ({ teamId: f.teamId, name: f.name, story: f.description }));

  return {
    shops,
    independents,
    closed,
    counts: {
      shopsBefore: before.length,
      shopsAfter: after.length,
      seatsBefore: before.reduce((n, t) => n + t.driverIds.length, 0),
      seatsAfter: after.reduce((n, t) => n + t.driverIds.length, 0),
      independentsAfter: independents.length,
      rosterAfter: offseason.roster.length,
      refilled,
      rookiesIntoCharters,
      lostLicence: offseason.demotions.length,
      retired: offseason.retirements.length,
      died: career.season.fatalities.length,
      replacedMidSeason: career.season.rookies.length,
      rookiesSigned: offseason.rookies.length,
      sold: offseason.teamFailures.filter((f) => f.replacedBy).length,
    },
  };
}

/**
 * The one sentence that answers the question Howard actually asked.
 *
 * ⚠ IT SAYS WHETHER THE FIELD IS WHOLE, first and in words. A row of counters
 * is a thing you have to do arithmetic on; "the garage is the same size it was"
 * is the thing he wanted to know, and if it ever stops being true this line is
 * where it will say so.
 */
export function garageLine(change: GarageChange): string {
  const c = change.counts;
  const same = c.shopsAfter === c.shopsBefore && c.seatsAfter === c.seatsBefore;
  const head = same
    ? `The garage comes through the winter the same size it went in — ${c.shopsAfter} shops, ${c.seatsAfter} chartered seats.`
    : `The garage changed size over the winter: ${c.shopsBefore} shops and ${c.seatsBefore} seats became ${c.shopsAfter} and ${c.seatsAfter}.`;

  if (c.refilled === 0) {
    return `${head} No charter seat emptied, so nobody had to be found for one.`;
  }
  const promoted = c.refilled - c.rookiesIntoCharters;
  const how =
    promoted > 0 && c.rookiesIntoCharters > 0
      ? `${promoted} filled by drivers moving up and ${c.rookiesIntoCharters} by rookies`
      : promoted > 0
        ? 'every one of them filled by a driver moving up'
        : 'every one of them filled by a rookie';
  return (
    `${head} ${c.refilled} seat${c.refilled === 1 ? '' : 's'} emptied and ${c.refilled === 1 ? 'was' : 'were'} filled again — ` +
    `${how}. ${c.independentsAfter} driver${c.independentsAfter === 1 ? '' : 's'} run without a charter.`
  );
}
